import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, Upload, X, AlertCircle, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card } from '../../components/ui/card';

/**
 * Public-facing activation form — gated by payment status.
 * Captures business details + logo + photos, posts multipart.
 */
const BASE = process.env.REACT_APP_BACKEND_URL;
const MAX_PHOTOS = 10;
const LOGO_MAX_BYTES = 2 * 1024 * 1024;   // 2 MB (matches backend)
const PHOTO_MAX_BYTES = 5 * 1024 * 1024;  // 5 MB (matches backend)
const ALLOWED_IMG_EXT = ['.jpg', '.jpeg', '.png', '.webp'];

/**
 * Client-side validator for image uploads — matches backend constraints
 * exactly so the user gets a friendly toast instead of a cryptic 400.
 * Returns true if the file is acceptable.
 */
const validateImage = (file, { maxBytes, kind }) => {
  if (!file) return false;
  const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
  const isImageMime = (file.type || '').startsWith('image/');
  if (!isImageMime || !ALLOWED_IMG_EXT.includes(ext)) {
    toast.error(`${kind} must be a JPG, PNG, or WEBP image.`);
    return false;
  }
  if (file.size > maxBytes) {
    const mb = (file.size / (1024 * 1024)).toFixed(1);
    const cap = (maxBytes / (1024 * 1024)).toFixed(0);
    toast.error(`${kind} is ${mb} MB — must be under ${cap} MB.`);
    return false;
  }
  return true;
};

export default function ActivationFormPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [gateLoading, setGateLoading] = useState(true);
  const [gateError, setGateError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [logo, setLogo] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [form, setForm] = useState({
    business_name: '', owner_name: '', mobile: '', email: '',
    domain_pref_1: '', domain_pref_2: '', domain_pref_3: '',
    whatsapp: '', phone: '', social_links: '',
  });

  useEffect(() => {
    fetch(`${BASE}/api/smb/activate/${token}/status`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(j => {
        if (j.status === 'activation_link_sent') {
          setGateError('Please complete the payment first.');
          setTimeout(() => navigate(`/activate/${token}`), 1500);
          return;
        }
        if (j.status === 'details_submitted' || ['in_development','awaiting_approval','approved','paid','website_live'].includes(j.status)) {
          navigate(`/activate/${token}/thank-you`); return;
        }
        // status === 'activation_paid' — show form
        setGateLoading(false);
      })
      .catch(() => { setGateError('This link is invalid or expired.'); setGateLoading(false); });
  }, [token, navigate]);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onPickLogo = (file) => {
    if (!file) { setLogo(null); return; }
    if (validateImage(file, { maxBytes: LOGO_MAX_BYTES, kind: 'Logo' })) {
      setLogo(file);
    }
  };

  const handlePhotos = (e) => {
    const incoming = Array.from(e.target.files);
    const slots = MAX_PHOTOS - photos.length;
    if (incoming.length > slots) {
      toast.error(`You can add ${slots} more photo${slots === 1 ? '' : 's'} (max ${MAX_PHOTOS}).`);
    }
    const accepted = [];
    for (const f of incoming.slice(0, slots)) {
      if (validateImage(f, { maxBytes: PHOTO_MAX_BYTES, kind: `Photo "${f.name}"` })) {
        accepted.push(f);
      }
    }
    if (accepted.length) setPhotos([...photos, ...accepted]);
    e.target.value = '';
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    // Final safety net — re-validate before upload in case state was set
    // through an older code path or browser auto-fill.
    if (logo && !validateImage(logo, { maxBytes: LOGO_MAX_BYTES, kind: 'Logo' })) return;
    for (const p of photos) {
      if (!validateImage(p, { maxBytes: PHOTO_MAX_BYTES, kind: `Photo "${p.name}"` })) return;
    }
    setSubmitting(true);
    setSubmitError('');
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (logo) fd.append('logo', logo);
    photos.forEach(p => fd.append('photos', p));
    try {
      const r = await fetch(`${BASE}/api/smb/activate/${token}/submit-form`, { method: 'POST', body: fd });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || 'Submission failed');
      }
      navigate(`/activate/${token}/thank-you`);
    } catch (e) {
      setSubmitError(e.message);
      setSubmitting(false);
    }
  };

  if (gateLoading) return <Centered><Loader2 className="h-8 w-8 animate-spin text-emerald-500" /></Centered>;
  if (gateError) return <Centered><Card className="p-6 text-center space-y-2"><AlertCircle className="h-10 w-10 mx-auto text-rose-500" /><p>{gateError}</p></Card></Centered>;

  return (
    <div className="min-h-screen bg-background py-10 px-4">
      <form onSubmit={submit} className="max-w-2xl mx-auto space-y-6">
        <header className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-600 text-xs font-semibold mb-3">
            <Check className="h-3.5 w-3.5" />Payment received
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold">A few details to kick off</h1>
          <p className="text-muted-foreground mt-2 text-sm">Help us tailor your website. Takes 3 minutes.</p>
        </header>

        <Card className="p-5 space-y-4">
          <h2 className="font-semibold">Business</h2>
          <Field label="Business Name *"><Input required value={form.business_name} onChange={setField('business_name')} data-testid="form-business-name" /></Field>
          <Field label="Owner Name *"><Input required value={form.owner_name} onChange={setField('owner_name')} data-testid="form-owner-name" /></Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Mobile *"><Input required value={form.mobile} onChange={setField('mobile')} data-testid="form-mobile" /></Field>
            <Field label="Email *"><Input required type="email" value={form.email} onChange={setField('email')} data-testid="form-email" /></Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="WhatsApp Number"><Input value={form.whatsapp} onChange={setField('whatsapp')} data-testid="form-whatsapp" /></Field>
            <Field label="Landline / Phone"><Input value={form.phone} onChange={setField('phone')} data-testid="form-phone" /></Field>
          </div>
        </Card>

        <Card className="p-5 space-y-3">
          <h2 className="font-semibold">Preferred Domain</h2>
          <p className="text-xs text-muted-foreground">List 3 options in priority order. We&apos;ll check availability and confirm.</p>
          <Field label="Preference #1 *"><Input required placeholder="e.g. dazzlestation.com" value={form.domain_pref_1} onChange={setField('domain_pref_1')} data-testid="form-domain-1" /></Field>
          <Field label="Preference #2"><Input placeholder="e.g. dazzlestationsalon.in" value={form.domain_pref_2} onChange={setField('domain_pref_2')} data-testid="form-domain-2" /></Field>
          <Field label="Preference #3"><Input placeholder="e.g. mydazzlestation.com" value={form.domain_pref_3} onChange={setField('domain_pref_3')} data-testid="form-domain-3" /></Field>
        </Card>

        <Card className="p-5 space-y-3">
          <h2 className="font-semibold">Brand assets</h2>
          <Field label="Logo (max 2 MB · JPG/PNG/WEBP)">
            <FileChip file={logo} onPick={onPickLogo} accept="image/*" testId="form-logo" />
          </Field>
          <Field label={`Photos (up to ${MAX_PHOTOS} · 5 MB each)`}>
            <div className="space-y-2">
              {photos.map((p, i) => (
                <div key={i} className="flex items-center justify-between bg-muted/40 rounded-md px-3 py-1.5 text-xs">
                  <span className="truncate">{p.name}</span>
                  <button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="text-rose-500"><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              {photos.length < MAX_PHOTOS && (
                <label className="flex items-center justify-center gap-2 border-2 border-dashed border-border rounded-md py-3 text-sm cursor-pointer hover:bg-muted/30">
                  <Upload className="h-4 w-4" />Add photos
                  <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotos} data-testid="form-photos" />
                </label>
              )}
            </div>
          </Field>
          <Field label="Social media links (one per line)">
            <Textarea rows={3} placeholder="https://instagram.com/...&#10;https://facebook.com/..." value={form.social_links} onChange={setField('social_links')} data-testid="form-social" />
          </Field>
        </Card>

        {submitError && <Card className="p-3 text-sm text-rose-500 bg-rose-500/10 border-rose-500/30 flex items-center gap-2"><AlertCircle className="h-4 w-4 shrink-0" />{submitError}</Card>}

        <Button type="submit" size="lg" className="w-full h-14 bg-emerald-500 hover:bg-emerald-600" disabled={submitting} data-testid="form-submit">
          {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting…</> : 'Submit & start my project'}
        </Button>
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs">{label}</Label>
    {children}
  </div>
);

const FileChip = ({ file, onPick, accept, testId }) => (
  file ? (
    <div className="flex items-center justify-between bg-muted/40 rounded-md px-3 py-2 text-sm">
      <span className="truncate">{file.name}</span>
      <button type="button" onClick={() => onPick(null)} className="text-rose-500"><X className="h-4 w-4" /></button>
    </div>
  ) : (
    <label className="flex items-center justify-center gap-2 border-2 border-dashed border-border rounded-md py-3 text-sm cursor-pointer hover:bg-muted/30">
      <Upload className="h-4 w-4" />Choose file
      <input type="file" accept={accept} className="hidden" onChange={e => onPick(e.target.files[0] || null)} data-testid={testId} />
    </label>
  )
);

const Centered = ({ children }) => (
  <div className="min-h-screen bg-background flex items-center justify-center p-4"><div className="max-w-md w-full">{children}</div></div>
);
